/* kn-collection.js · PixelNova Solutions · 10/8/2026 */
(() => {
  if (window.knCollectionsInitialized) return;
  window.knCollectionsInitialized = true;
  class Collection {
    constructor(root) {
      this.root = root;
      this.request = null;
      this.drawerOpen = false;
      this.mobile = window.matchMedia('(max-width: 850px)');
      root.classList.add('kn-collection--js');
      root.addEventListener('submit', event => {
        if (!event.target.matches('[data-kn-form]')) return;
        event.preventDefault();
        if (!event.target.reportValidity()) return;
        const url = this.formURL(event.target);
        this.closeDrawer(false);
        this.navigate(url, true);
      });
      root.addEventListener('change', event => {
        if (event.target.matches('[data-kn-kit]')) this.changeKit(event.target);
        if (event.target.matches('.kn-facet__value input[type=checkbox]') && !this.mobile.matches) {
          if (root.querySelector('[data-kn-form]').reportValidity()) this.navigate(this.formURL(root.querySelector('[data-kn-form]')), true, event.target.id);
        }
        if (event.target.matches('[data-kn-sort]')) {
          if (!root.querySelector('[data-kn-form]').reportValidity()) return;
          this.navigate(this.formURL(root.querySelector('[data-kn-form]')), true, event.target.id);
        }
      });
      root.addEventListener('click', event => {
        if (event.target.closest('[data-kn-open]')) this.openDrawer();
        if (event.target.closest('[data-kn-close],[data-kn-shade]')) this.closeDrawer();
        const link = event.target.closest('a[data-kn-nav],a[data-kn-clear]');
        if (!link || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        const url = new URL(link.href, location.href);
        const current = new URL(location.href);
        current.searchParams.forEach((value, key) => {
          if (!key.startsWith('filter.') && !['page', 'sort_by', 'section_id', 'sections'].includes(key) && !url.searchParams.has(key)) url.searchParams.append(key, value);
        });
        if (link.hasAttribute('data-kn-clear')) {
          Array.from(url.searchParams.keys()).filter(key => key.startsWith('filter.')).forEach(key => url.searchParams.delete(key));
          url.searchParams.delete('page');
        }
        this.closeDrawer(false);
        this.navigate(url, true);
      });
      document.addEventListener('keydown', event => this.keydown(event));
      this.mobile.addEventListener('change', () => { if (!this.mobile.matches) this.closeDrawer(false); });
      window.addEventListener('popstate', () => {
        this.closeDrawer(false);
        this.navigate(new URL(location.href), false);
      });
    }
    formURL(form) {
      const url = new URL(form.action, location.href);
      const current = new URL(location.href);
      current.searchParams.forEach((value, key) => {
        if (!['page', 'section_id', 'sections', 'sort_by'].includes(key)) url.searchParams.append(key, value);
      });
      const names = new Set(Array.from(form.elements).map(input => input.name).filter(Boolean));
      names.forEach(name => url.searchParams.delete(name));
      for (const [name, value] of new FormData(form)) if (String(value).trim() !== '') url.searchParams.append(name, value);
      return url;
    }
    async navigate(url, push, focusId) {
      if (url.origin !== location.origin || url.pathname !== new URL(this.root.querySelector('form').action).pathname) {
        location.assign(url.href);
        return;
      }
      this.request?.abort();
      const controller = new AbortController();
      this.request = controller;
      const expanded = new Map(Array.from(this.root.querySelectorAll('[data-kn-facet]')).map(facet => [facet.dataset.knFacet, facet.open]));
      this.root.setAttribute('aria-busy', 'true');
      this.root.querySelector('[data-kn-status]').textContent = 'Loading products.';
      const fetchURL = new URL(url);
      fetchURL.searchParams.set('section_id', this.root.dataset.sectionId);
      try {
        const response = await fetch(fetchURL, { signal: controller.signal, credentials: 'same-origin' });
        if (!response.ok) throw new Error('Collection response unavailable');
        const documentResult = new DOMParser().parseFromString(await response.text(), 'text/html');
        const replacement = documentResult.getElementById(this.root.id);
        if (!replacement) throw new Error('Collection section unavailable');
        if (controller.signal.aborted) return;
        this.root.innerHTML = replacement.innerHTML;
        this.root.querySelectorAll('[data-kn-facet]').forEach(facet => {
          if (expanded.has(facet.dataset.knFacet)) facet.open = expanded.get(facet.dataset.knFacet);
        });
        if (push && url.href !== location.href) history.pushState({}, '', url.href);
        const count = this.root.querySelector('[data-kn-results-heading]');
        this.root.querySelector('[data-kn-status]').textContent = `${count.textContent.trim()} loaded.`;
        const focus = focusId ? document.getElementById(focusId) : count;
        focus?.focus({ preventScroll: true });
        if (!focusId) this.root.querySelector('.kn-results').scrollIntoView({ block: 'start', behavior: 'instant' });
      } catch (error) {
        if (error.name !== 'AbortError') location.assign(url.href);
      } finally {
        if (this.request === controller) {
          this.root.removeAttribute('aria-busy');
          this.request = null;
        }
      }
    }
    changeKit(select) {
      const option = select.selectedOptions[0];
      const card = select.closest('[data-kn-card]');
      card.querySelectorAll('[data-kn-product-link]').forEach(link => { link.href = option.dataset.url; });
      card.querySelector('[data-kn-product-price]').textContent = option.dataset.price;
      card.querySelector('[data-kn-product-size]').textContent = option.dataset.size;
      const image = card.querySelector('[data-kn-product-image]');
      if (image && option.dataset.image) {
        image.src = option.dataset.image;
        image.alt = option.dataset.alt;
        image.width = Number(option.dataset.width);
        image.height = Number(option.dataset.height);
      }
    }
    openDrawer() {
      if (!this.mobile.matches || this.drawerOpen) return;
      this.drawerOpen = true;
      const drawer = this.root.querySelector('[data-kn-drawer]');
      drawer.classList.add('kn-filters--open');
      drawer.setAttribute('role', 'dialog');
      drawer.setAttribute('aria-modal', 'true');
      drawer.setAttribute('aria-labelledby', drawer.querySelector('h2').id);
      this.root.querySelector('[data-kn-shade]').hidden = false;
      this.root.querySelector('[data-kn-open]').setAttribute('aria-expanded', 'true');
      this.previousOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      drawer.querySelector('[data-kn-close]').focus();
    }
    closeDrawer(restoreFocus = true) {
      if (!this.drawerOpen) return;
      this.drawerOpen = false;
      const drawer = this.root.querySelector('[data-kn-drawer]');
      drawer.classList.remove('kn-filters--open');
      drawer.querySelectorAll('input').forEach(input => {
        if (input.type === 'checkbox') input.checked = input.defaultChecked;
        else input.value = input.defaultValue;
        input.setCustomValidity('');
      });
      ['role', 'aria-modal', 'aria-labelledby'].forEach(name => drawer.removeAttribute(name));
      this.root.querySelector('[data-kn-shade]').hidden = true;
      const button = this.root.querySelector('[data-kn-open]');
      button.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = this.previousOverflow;
      if (restoreFocus) button.focus();
    }
    keydown(event) {
      if (!this.drawerOpen) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        this.closeDrawer();
      }
      if (event.key !== 'Tab') return;
      const drawer = this.root.querySelector('[data-kn-drawer]');
      const items = Array.from(drawer.querySelectorAll('a[href],button:not([disabled]),input:not([disabled]):not([type=hidden]),select,summary')).filter(item => item.getClientRects().length);
      const first = items[0], last = items[items.length - 1];
      if (event.shiftKey && (document.activeElement === first || !drawer.contains(document.activeElement))) {
        event.preventDefault(); last.focus();
      } else if (!event.shiftKey && (document.activeElement === last || !drawer.contains(document.activeElement))) {
        event.preventDefault(); first.focus();
      }
    }
  }
  const init = () => document.querySelectorAll('[data-kn-collection]').forEach(root => {
    if (!root.dataset.knBound) { root.dataset.knBound = 'true'; new Collection(root); }
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true }); else init();
  document.addEventListener('shopify:section:load', init);
})();
